import { CourseOffer, OccurrenceOffer, Offer } from '@shared/types';
import { FunctionComponent, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { RadioGroup } from '../../../../../RadioGroup';
import Card from '../../../../../Card';
import { isOfferDateValid } from '../../../../shared/isOfferDateValid';
import { LocalDate } from '../../../../../LocalDate';
import { Temporal } from 'temporal-polyfill';
import { renderPrice } from '../../../../../../services/renderPrice';
import { SupportedCurrencies } from '@cademy-package/sdk-component-types';

export type OfferSelectProps = {
    offers: Array<CourseOffer | OccurrenceOffer>;
    selectedOffer: string;
    hidePricing?: boolean;
    setSelectedOffer: (selectedOffer: string) => unknown;
    currency: SupportedCurrencies;
};

export const OfferSelect: FunctionComponent<OfferSelectProps> = ({
    offers,
    selectedOffer,
    hidePricing,
    setSelectedOffer,
    currency,
}) => {
    const now = Date.now() / 1000;
    const value = useMemo(() => {
        return offers.find((offer) => offer._id === selectedOffer);
    }, [offers, selectedOffer]);

    const displayPrice = useCallback(
        (price: Offer['price']) => {
            if (price === undefined) return null;
            if (hidePricing) return null;
            const priceLabel = renderPrice(price, currency);
            return <span className={styles.price}>{priceLabel}</span>;
        },
        [hidePricing, currency]
    );

    const isDisabled = useCallback((offer: CourseOffer | OccurrenceOffer) => {
        return !isOfferDateValid(offer);
    }, []);

    const onChange = useCallback(
        (offer: CourseOffer | OccurrenceOffer) => {
            if (isDisabled(offer)) return;
            setSelectedOffer(offer._id);
        },
        [isDisabled, setSelectedOffer]
    );

    return (
        <RadioGroup value={value} options={offers} onChange={onChange}>
            {(offer, isSelected, isFocused) => {
                const className = classNames(styles.option, {
                    [styles.selected]: isSelected,
                    [styles.focused]: isFocused,
                });
                return (
                    <Card className={className}>
                        <div className={classNames(styles.headerBar)}>
                            <input
                                type="radio"
                                checked={isSelected}
                                readOnly
                                disabled={isDisabled(offer)}
                            />
                            <span className={styles.name}>{offer.name || ''}</span>
                            <div>{displayPrice(offer.price)}</div>
                        </div>
                        <p className={styles.description}>{offer.description}</p>
                        {offer.start_at && now < offer.start_at ? (
                            <p className={styles.saleDate}>
                                Sales start on{' '}
                                {
                                    <LocalDate
                                        date={Temporal.Instant.fromEpochSeconds(
                                            offer.start_at
                                        ).toString()}
                                        format={{
                                            day: 'numeric',
                                            month: 'short',
                                            hour: 'numeric',
                                            minute: 'numeric',
                                            timeZoneName: 'short',
                                        }}
                                    />
                                }
                            </p>
                        ) : null}
                        {offer.end_at && now > offer.end_at ? (
                            <p className={styles.saleDate}>
                                Sales ended on{' '}
                                {
                                    <LocalDate
                                        date={Temporal.Instant.fromEpochSeconds(
                                            offer.end_at
                                        ).toString()}
                                        format={{
                                            day: 'numeric',
                                            month: 'short',
                                            hour: 'numeric',
                                            minute: 'numeric',
                                            timeZoneName: 'short',
                                        }}
                                    />
                                }
                            </p>
                        ) : null}
                    </Card>
                );
            }}
        </RadioGroup>
    );
};
